import { Editor } from 'grapesjs';
import { RssComponentI18nAttributes, RssRenderer } from '@loyalty/editor-components';
import { faRss } from '@fortawesome/pro-solid-svg-icons/faRss';
import { getLocalizedProperty } from '@loyalty/shared-common';
import { EditorComponentDefinition } from '../types';
import { EditorContext } from '../../../services/editor-service';
import { I18nContent, Toolbar, buildParticipantContextFromGrape } from '../../utils';
import { RSSWidgetModal } from './RSSWidgetModal';
import { RSSWidgetTraits, rssWidgetTraitsSchema } from './RSSWidgetTraits';
import { setInnerHTML } from '../utils/set-inner-html';
import { translateAttributes } from '../../../services/copy-paste/copy-paste-utils';

export const rssComponentDefinition: EditorComponentDefinition = {
  id: 'rss',
  icon: faRss,
  category: 'Interactive elements',
  getLabel: ({ t }) => t('editor.component.rss'),
  component: Component,
  traits: {
    schema: rssWidgetTraitsSchema,
    form: RSSWidgetTraits,
  },
  copyPasteHelper: (element, context) => translateAttributes(element, context.destinationLocales),
};

function Component(editor: Editor) {
  const i18n = editor.I18n;
  const context: EditorContext = editor.Config.context;

  const languages = context?.languages || [];

  const i18nAttributes: I18nContent = Object.fromEntries(
    languages.map((l: string) => [
      l,
      { content: '', template: '' } satisfies RssComponentI18nAttributes,
    ]),
  );

  editor.DomComponents.addType(rssComponentDefinition.id, {
    model: {
      defaults: {
        droppable: false,
        draggable: '[data-gjs-type=column]',
        editable: true,
        traits: [],
        i18nAttributes,
      },
      initToolbar: Toolbar,
      toHTML(): string {
        return RssRenderer(
          // TODO this could become generic at some point, because we should do the same for all widgets
          {
            state: context.widgetState,
            options: {
              id: this.getId(),
              attributes: this.getAttributes(),
              i18nAttributes: this.attributes.i18nAttributes || {},
            },
            context: buildParticipantContextFromGrape(i18n),
          },
          editor.Canvas.getDocument(),
        );
      },
    },
    view: {
      onRender() {
        const { el, model } = this;
        setInnerHTML(el, model.toHTML());
      },
      events: {
        dblclick: 'onDoubleClick',
      },
      onDoubleClick() {
        const { i18nAttributes } = this.model.attributes;
        const locale = i18n.getLocale();
        const template =
          getLocalizedProperty({ i18nAttributes }, 'template', locale) ||
          getLocalizedProperty({ i18nAttributes }, 'content', locale);
        const feedUrl =
          getLocalizedProperty({ i18nAttributes }, 'feedUrl', locale) ||
          this.model.attributes.attributes.feedUrl;

        (editor.getConfig('context') as EditorContext).openModal(props => ({
          content: (
            <RSSWidgetModal
              {...props}
              template={template}
              feedUrl={feedUrl}
              callback={data => {
                this.model.addAttributes({ template: data });
                this.model.attributes.i18nAttributes = {
                  ...this.model.attributes.i18nAttributes,
                  [locale]: {
                    ...this.model.attributes.i18nAttributes?.[locale],
                    template: data,
                  },
                };
                this.onRender?.();
              }}
            />
          ),
          dialogProps: {
            maxWidth: 'xl',
          },
        }));
      },
    },
  });
}
